import { useState, useCallback, type FC, type MouseEvent } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { SeoLink } from 'dibs-seo/exports/SeoLink.tsx';
import { ALWAYS_LINKABLE_LINK } from 'dibs-elements/exports/SeoLink.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import dibsCss from 'dibs-css';
import {
    trackEcommerce,
    trackEvent,
    interactionTypeConstants,
    stepInteractionConstants,
    eventNameConstants,
} from 'dibs-tracking';

import styles from './SiteNav-style.scss';

import { type SiteNavDropdownImageModule_navigationModule$key } from './__generated__/SiteNavDropdownImageModule_navigationModule.graphql.ts';

// Current image container has a width to height ratio of 2:1
const CONTAINER_HEIGHT_RATIO_FACTOR = 1;
const CONTAINER_WIDTH_RATIO_FACTOR = 2;

const { EVENT_SELECT_PROMOTION, EVENT_NAVIGATION } = eventNameConstants;
const { INTERACTION_TYPE_GLOBAL_NAV } = interactionTypeConstants;
const { STEP_INTERACTION_GLOBAL_NAV_DROPDOWN_CLICK } = stepInteractionConstants;

type Props = {
    navigationModuleRef: SiteNavDropdownImageModule_navigationModule$key;
    isVisible: boolean;
};

export const SiteNavDropdownImageModule: FC<Props> = ({
    navigationModuleRef,
    isVisible = false,
}) => {
    const [imageIsLoaded, setImageIsLoaded] = useState(false);
    const navigationModule = useFragment(
        graphql`
            fragment SiteNavDropdownImageModule_navigationModule on NavigationModule {
                imageLinkData {
                    path
                    ...SeoLink_linkData
                }
                imageUrl
                imageTitlePrimary
                imageTitleSecondary
                imageHeight
                imageWidth
            }
        `,
        navigationModuleRef
    );

    const {
        imageUrl,
        imageLinkData,
        imageTitlePrimary,
        imageTitleSecondary,
        imageHeight,
        imageWidth,
    } = navigationModule;
    const { path } = imageLinkData || {};

    const handleImageLoad = useCallback(() => {
        setImageIsLoaded(true);
    }, []);

    const handleClick = (event: MouseEvent): void => {
        trackEcommerce({
            type: 'promoClick',
            promotions: [
                {
                    id: imageTitlePrimary?.toLowerCase() || '',
                    name: imageTitlePrimary || '',
                    creative: imageLinkData?.path || '',
                    position: 'global nav',
                },
            ],
            eventName: EVENT_SELECT_PROMOTION,
        });
        trackEvent(
            {
                category: 'navigation',
                action: 'header navigation clicked',
                label: 'trending now',
                eventName: EVENT_NAVIGATION,
                interaction_type: INTERACTION_TYPE_GLOBAL_NAV,
                step_interaction_name: STEP_INTERACTION_GLOBAL_NAV_DROPDOWN_CLICK,
                trigger: 'trending now',
            },
            event
        );
    };

    if (!imageUrl) {
        return null;
    }

    const containerRatio = CONTAINER_WIDTH_RATIO_FACTOR / CONTAINER_HEIGHT_RATIO_FACTOR;
    const imageWidthNum = Number(imageWidth?.replace('px', '') || '0');
    const imageHeightNum = Number(imageHeight?.replace('px', '') || '0');
    const imageRatio = imageWidthNum / imageHeightNum;

    const imageClasses = classnames(
        styles.imageModuleImage,
        dibsCss.absolute,
        dibsCss.left50,
        dibsCss.top50,
        dibsCss.right0,
        imageIsLoaded ? dibsCss.opacity100 : dibsCss.opacity0,
        // If ratio of image is less than container ratio,
        // need to set image width to 100% (with variable height).
        // This is because any image with a width less than twice
        // it's height - even landscape images - will not fill
        // container if the image height is set to 100% (with variable width)
        // Figured out with the help of:
        // https://medium.com/@elad/how-to-crop-images-with-css-b8471d402b16
        imageRatio < containerRatio
            ? [dibsCss.hAuto, dibsCss.wFull]
            : [dibsCss.hFull, dibsCss.wAuto]
    );

    const imageEl = (
        <div
            className={classnames(
                dibsCss.flex,
                dibsCss.justifyEnd,
                dibsCss.relative,
                dibsCss.hFull,
                dibsCss.wFull
            )}
        >
            <div
                className={classnames(
                    styles.imageBox,
                    dibsCss.h200px,
                    dibsCss.mlAuto,
                    dibsCss.overflowHidden,
                    dibsCss.relative,
                    dibsCss.roundedXxsmall
                )}
            >
                {(isVisible || imageIsLoaded) && (
                    <img
                        className={imageClasses}
                        src={imageUrl}
                        alt={imageTitlePrimary || ''}
                        onLoad={handleImageLoad}
                    />
                )}
            </div>
            <div
                className={classnames(
                    styles.titleBox,
                    dibsCss.textNoir,
                    dibsCss.bgWhite,
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.flexWrap,
                    dibsCss.absolute,
                    dibsCss.mtSmaller,
                    dibsCss.rightNone
                )}
            >
                {imageTitleSecondary && (
                    <div
                        className={classnames(
                            dibsCss.sassyFontBodySizeSmall,
                            dibsCss.textTrademember,
                            dibsCss.wFull
                        )}
                    >
                        {imageTitleSecondary}
                    </div>
                )}
                <div className={classnames(dibsCss.sassyFontBodySizeSmallTypeHeavy, dibsCss.wFull)}>
                    {imageTitlePrimary}
                </div>
                <div className={classnames(dibsCss.cursorPointer, styles.textUnderline)}>
                    <FormattedMessage id="siteNav.shopNow" defaultMessage="Shop Now" />
                </div>
            </div>
        </div>
    );

    if (path) {
        return (
            <SeoLink
                className={styles.imageModuleLink}
                onClick={handleClick}
                variant={ALWAYS_LINKABLE_LINK}
                linkData={imageLinkData}
            >
                {imageEl}
            </SeoLink>
        );
    }

    return imageEl;
};
